/**
 * Wealthica Storage Utility - Clean & Focused
 * Simple utility for managing Wealthica-related storage.
 * Uses hybrid sessionStorage/localStorage strategy for reliability.
 * Atomic operations prevent race conditions during logout.
 * 
 * @version WEALTHICA2-CLEAN-REFACTOR-AUG2025
 */

import { WealthicaConnection } from '../types';
import { logger } from '@/services/LoggingService';
import { CallbackUtils } from '@/utils/CallbackFlowManager';

// Storage keys
const WEALTHICA_TOKEN_KEY = 'wealthica2_bearer_token';
const WEALTHICA_CONNECTION_DATA_KEY = 'wealthica2_connection_data';
const WEALTHICA_CONNECTION_CONFIRMED_KEY = 'wealthica2_connection_confirmed';
const POSITIONS_CACHE_PREFIX = 'wealthica2_positions_cache';
const ACCOUNTS_CACHE_PREFIX = 'wealthica2_accounts_cache';

export const WealthicaStorage = {
  /**
   * Generic storage access - tries sessionStorage first, then localStorage
   */
  getItem(key: string): string | null {
    try {
      return sessionStorage.getItem(key) || localStorage.getItem(key);
    } catch (error) {
      logger.error('WealthicaStorage.getItem', 'Storage access failed', { key, error });
      return null;
    }
  },

  /**
   * Generic storage write - writes to sessionStorage and optionally localStorage
   */
  setItem(key: string, value: string, persistent: boolean = false): void {
    try {
      sessionStorage.setItem(key, value);
      if (persistent) {
        localStorage.setItem(key, value);
      }
    } catch (error) {
      logger.error('WealthicaStorage.setItem', 'Storage write failed', { key, persistent, error });
    }
  },

  /**
   * Generic storage removal - removes from both storages
   */
  removeItem(key: string): void {
    try {
      sessionStorage.removeItem(key);
      localStorage.removeItem(key);
    } catch (error) {
      logger.error('WealthicaStorage.removeItem', 'Storage removal failed', { key, error });
    }
  },

  /**
   * Initialize storage and clean up stale data
   * Uses CallbackFlowManager to prevent clearing during any provider's OAuth callback
   */
  initializeStorage(): void {
    try {
      // Use CallbackFlowManager for comprehensive callback protection
      if (!CallbackUtils.isSafeForProviderOperation('wealthica2', 'initializeStorage')) {
        const debugInfo = CallbackUtils.getCallbackDebugInfo();
        logger.info('WealthicaStorage', 'Skipping initialization cleanup due to active OAuth callbacks', {
          callbackInfo: debugInfo,
          activeFlows: debugInfo.flows?.map((flow: any) => flow.provider)
        });
        return;
      }

      // Only clear session-specific data that might be stale
      sessionStorage.removeItem('wealthica2_page_loaded');
      logger.debug('WealthicaStorage.initializeStorage', 'Cleared stale session data');
    } catch (error) {
      logger.error('WealthicaStorage.initializeStorage', 'Initialization failed', { error });
    }
  },

  /**
   * Token management
   */
  hasToken(): boolean {
    return this.getToken() !== null;
  },

  /**
   * Retrieves the stored Wealthica API token.
   * 
   * @returns {string | null} The Wealthica token (can be JWT format) or null if not found
   * 
   * @note IMPORTANT: Wealthica API v2 returns JWT tokens (starting with 'ey') which are legitimate
   * bearer tokens for Wealthica API calls. This is NOT contamination - it's the expected format.
   */
  getToken(): string | null {
    try {
      const token = this.getItem(WEALTHICA_TOKEN_KEY);
      
      // DEBUGGING: Log token retrieval details without false warnings
      if (token) {
        const isJWT = token.startsWith('ey');
        logger.debug('WealthicaStorage.getToken', 'Wealthica token retrieved', {
          tokenLength: token.length,
          tokenPrefix: token.substring(0, 10) + '...',
          isJWT: isJWT,
          hasToken: !!token,
          tokenFormat: isJWT ? 'JWT (expected for Wealthica v2)' : 'Bearer token'
        });
      }
      
      return token && token.length > 0 ? token : null;
    } catch (error) {
      logger.error('WealthicaStorage.getToken', 'Token retrieval failed', { error });
      return null;
    }
  },

  /**
   * Stores a Wealthica API token securely with validation.
   * 
   * @param {string | null} token - The Wealthica API token to store (can be JWT format)
   * @param {boolean} persistent - Whether to persist to localStorage (default: true)
   * 
   * @note CRITICAL: Wealthica API v2 legitimately returns JWT tokens (starting with 'ey').
   * This method distinguishes between:
   * - ✅ ALLOWED: Wealthica JWT tokens (legitimate API tokens)
   * - ❌ BLOCKED: Supabase JWT tokens (contamination prevention)
   * - ❌ BLOCKED: Malformed JWT tokens (security)
   * 
   * @throws {Error} If Supabase JWT or malformed JWT is provided
   */
  setToken(token: string | null, persistent: boolean = true): void {
    try {
      if (token) {
        // CRITICAL PROTECTION: Prevent Supabase JWT contamination while allowing Wealthica JWTs
        const isJWT = token.startsWith('ey');
        const stackTrace = new Error().stack;
        
        logger.debug('WealthicaStorage.setToken', 'Wealthica token validation', {
          tokenLength: token.length,
          tokenPrefix: token.substring(0, 10) + '...',
          tokenSuffix: '...' + token.substring(token.length - 5),
          isJWT: isJWT,
          tokenFormat: isJWT ? 'JWT (expected for Wealthica v2)' : 'Bearer token',
          persistent
        });
        
        if (isJWT) {
          // Check if this is a Supabase JWT by examining the payload
          try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const isSupabaseJWT = payload.iss?.includes('supabase') || 
                                 payload.aud === 'authenticated' ||
                                 payload.role === 'authenticated';
            
            if (isSupabaseJWT) {
              logger.error('WealthicaStorage.setToken', '🚨 SUPABASE JWT CONTAMINATION BLOCKED!', {
                tokenPreview: token.substring(0, 50) + '...',
                stackTrace: stackTrace?.split('\n').slice(1, 8).join('\n'),
                rejectionReason: 'Supabase JWT tokens must not be stored as Wealthica tokens',
                issuer: payload.iss,
                audience: payload.aud,
                role: payload.role
              });
              
              throw new Error('SECURITY: Supabase JWT token rejected for Wealthica storage. Only Wealthica tokens are allowed.');
            } else {
              // This is a valid Wealthica JWT token - expected format for v2 API
              logger.debug('WealthicaStorage.setToken', '✅ Wealthica JWT token validated and accepted', {
                tokenLength: token.length,
                issuer: payload.iss || 'unknown',
                audience: payload.aud || 'unknown',
                note: 'JWT format is legitimate for Wealthica API v2'
              });
            }
          } catch (jwtDecodeError) {
            // If we can't decode the JWT, it might be malformed - reject it
            logger.error('WealthicaStorage.setToken', '🚨 MALFORMED JWT BLOCKED!', {
              tokenPreview: token.substring(0, 50) + '...',
              jwtDecodeError: jwtDecodeError instanceof Error ? jwtDecodeError.message : 'Unknown error',
              rejectionReason: 'Unable to decode JWT payload for validation'
            });
            
            throw new Error('SECURITY: Malformed JWT token rejected for Wealthica storage.');
          }
        }
        
        this.setItem(WEALTHICA_TOKEN_KEY, token, persistent);
        logger.debug('WealthicaStorage.setToken', 'Valid Wealthica token stored', { persistent });
      } else {
        this.removeItem(WEALTHICA_TOKEN_KEY);
        logger.debug('WealthicaStorage.setToken', 'Token cleared');
      }
    } catch (error) {
      logger.error('WealthicaStorage.setToken', 'Token storage failed', { error });
      throw error; // Re-throw to propagate JWT rejection errors
    }
  },

  /**
   * Connection data management
   */
  getConnectionData(): WealthicaConnection | null {
    try {
      const data = this.getItem(WEALTHICA_CONNECTION_DATA_KEY);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      logger.error('WealthicaStorage.getConnectionData', 'Connection data retrieval failed', { error });
      return null;
    }
  },

  setConnectionData(data: WealthicaConnection | null, persistent: boolean = true): void {
    try {
      if (data) {
        this.setItem(WEALTHICA_CONNECTION_DATA_KEY, JSON.stringify(data), persistent);
        logger.debug('WealthicaStorage.setConnectionData', 'Connection data stored', { persistent });
      } else {
        this.removeItem(WEALTHICA_CONNECTION_DATA_KEY);
        logger.debug('WealthicaStorage.setConnectionData', 'Connection data cleared');
      }
    } catch (error) {
      logger.error('WealthicaStorage.setConnectionData', 'Connection data storage failed', { error });
    }
  },

  /**
   * Connection confirmation management
   */
  getConnectionConfirmed(): boolean {
    try {
      const confirmed = this.getItem(WEALTHICA_CONNECTION_CONFIRMED_KEY);
      return confirmed === 'true';
    } catch (error) {
      logger.error('WealthicaStorage.getConnectionConfirmed', 'Confirmation retrieval failed', { error });
      return false;
    }
  },

  setConnectionConfirmed(confirmed: boolean): void {
    try {
      this.setItem(WEALTHICA_CONNECTION_CONFIRMED_KEY, confirmed.toString(), true);
      logger.debug('WealthicaStorage.setConnectionConfirmed', 'Confirmation stored', { confirmed });
    } catch (error) {
      logger.error('WealthicaStorage.setConnectionConfirmed', 'Confirmation storage failed', { error });
    }
  },

  /**
   * CRITICAL: Clear session data during logout to prevent race conditions
   * Preserves user authentication preferences AND cached data in localStorage
   * for instant restoration on login (like navigating within the app)
   */
  clearForLogout(): void {
    try {
      // Clear session-specific data that could cause race conditions
      sessionStorage.removeItem('wealthica2_page_loaded');
      
      // Clear institution-specific accounts cache from sessionStorage - preserve localStorage copy
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith(ACCOUNTS_CACHE_PREFIX)) {
          sessionStorage.removeItem(key);
        }
      });
      
      // Clear position cache from sessionStorage only (preserve localStorage cache)
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith(POSITIONS_CACHE_PREFIX)) {
          sessionStorage.removeItem(key);
        }
      });
      
      logger.info('WealthicaStorage.clearForLogout', 'Session data cleared, cached data and user preferences preserved', {
        clearedKeys: ['page_loaded', 'accounts_caches_session', 'position_caches_session'],
        preservedKeys: ['bearer_token', 'connection_data', 'connection_confirmed', 'cached_accounts_localStorage', 'cached_positions_localStorage']
      });
    } catch (error) {
      logger.error('WealthicaStorage.clearForLogout', 'Logout clearing failed', { error });
    }
  },

  /**
   * Clear cache data specific to a single institution
   * Used when disconnecting one institution while preserving others
   */
  clearInstitutionCache(institutionId: string): void {
    try {
      const keysToDelete: string[] = [];
      
      // Find institution-specific keys for both accounts and positions
      [sessionStorage, localStorage].forEach(storage => {
        Object.keys(storage).forEach(key => {
          if (key.includes(institutionId) || 
              key.startsWith(`${POSITIONS_CACHE_PREFIX}:${institutionId}`) ||
              key.startsWith(`${ACCOUNTS_CACHE_PREFIX}:${institutionId}`)) {
            keysToDelete.push(key);
            storage.removeItem(key);
          }
        });
      });
      
      logger.info('WealthicaStorage.clearInstitutionCache', 'Institution cache cleared', {
        institutionId,
        clearedKeys: keysToDelete
      });
    } catch (error) {
      logger.error('WealthicaStorage.clearInstitutionCache', 'Institution cache clearing failed', { 
        institutionId, 
        error 
      });
    }
  },

  /**
   * Clear only connection tokens for an institution (preserve cached data)
   * Used in "Stop Syncing" mode to preserve user's ability to work with data
   */
  clearConnectionTokens(institutionId: string): void {
    try {
      const keysToDelete: string[] = [];
      
      // Only clear connection-specific tokens and session data
      const connectionKeys = [
        'wealthica2_bearer_token',
        'wealthica2_connection_data',
        'wealthica2_page_loaded',
        'wealthica2_session_active'
      ];
      
      connectionKeys.forEach(key => {
        if (sessionStorage.getItem(key)) {
          sessionStorage.removeItem(key);
          keysToDelete.push(`sessionStorage.${key}`);
        }
        if (localStorage.getItem(key)) {
          localStorage.removeItem(key);
          keysToDelete.push(`localStorage.${key}`);
        }
      });
      
      logger.info('WealthicaStorage.clearConnectionTokens', 'Connection tokens cleared, cached data preserved', {
        institutionId,
        clearedKeys: keysToDelete
      });
    } catch (error) {
      logger.error('WealthicaStorage.clearConnectionTokens', 'Token clearing failed', { 
        institutionId, 
        error 
      });
    }
  },

  /**
   * Clear only connection data while preserving all cached accounts/positions
   * Used when last institution disconnects in "Stop Syncing" mode
   */
  clearConnectionDataOnly(): void {
    try {
      const keysToDelete: string[] = [];
      
      // Clear connection and session data but preserve all cached data
      const connectionOnlyKeys = [
        'wealthica2_bearer_token',
        'wealthica2_connection_data',
        'wealthica2_page_loaded',
        'wealthica2_session_active'
      ];
      
      connectionOnlyKeys.forEach(key => {
        if (sessionStorage.getItem(key)) {
          sessionStorage.removeItem(key);
          keysToDelete.push(`sessionStorage.${key}`);
        }
        if (localStorage.getItem(key)) {
          localStorage.removeItem(key);
          keysToDelete.push(`localStorage.${key}`);
        }
      });
      
      logger.info('WealthicaStorage.clearConnectionDataOnly', 'Connection data cleared, all cached data preserved', {
        clearedKeys: keysToDelete,
        note: 'Accounts and positions cache preserved for continued data access'
      });
    } catch (error) {
      logger.error('WealthicaStorage.clearConnectionDataOnly', 'Connection data clearing failed', { error });
    }
  },

  /**
   * Nuclear clear - remove EVERYTHING for clean slate when persistence is disabled
   * Used when user has persistence OFF and disconnects (expects complete reset)
   */
  clearEverything(): void {
    try {
      const clearedKeys: string[] = [];
      
      // Clear all Wealthica-related keys from both storages
      const wealthicaKeys = [
        WEALTHICA_TOKEN_KEY,
        WEALTHICA_CONNECTION_DATA_KEY,
        WEALTHICA_CONNECTION_CONFIRMED_KEY,
        'wealthica2_page_loaded',
        'wealthica2_session_active'
      ];
      
      wealthicaKeys.forEach(key => {
        if (sessionStorage.getItem(key)) {
          sessionStorage.removeItem(key);
          clearedKeys.push(`sessionStorage.${key}`);
        }
        if (localStorage.getItem(key)) {
          localStorage.removeItem(key);
          clearedKeys.push(`localStorage.${key}`);
        }
      });
      
      // Clear all cache keys (accounts and positions)
      [sessionStorage, localStorage].forEach(storage => {
        Object.keys(storage).forEach(key => {
          if (key.startsWith(ACCOUNTS_CACHE_PREFIX) || key.startsWith(POSITIONS_CACHE_PREFIX)) {
            storage.removeItem(key);
            clearedKeys.push(key);
          }
        });
      });
      
      logger.info('WealthicaStorage.clearEverything', 'Nuclear clear completed - all Wealthica data removed', {
        clearedKeys,
        note: 'Complete reset for clean slate when persistence is disabled'
      });
    } catch (error) {
      logger.error('WealthicaStorage.clearEverything', 'Nuclear clear failed', { error });
    }
  },

  /**
   * Internal utility: Clear all position cache keys
   * Used by other clearing methods
   */
  clearAllPositionCacheKeys(): void {
    try {
      const clearedKeys: string[] = [];
      
      [sessionStorage, localStorage].forEach(storage => {
        Object.keys(storage).forEach(key => {
          if (key.startsWith(POSITIONS_CACHE_PREFIX)) {
            storage.removeItem(key);
            clearedKeys.push(key);
          }
        });
      });
      
      if (clearedKeys.length > 0) {
        logger.debug('WealthicaStorage.clearAllPositionCacheKeys', 'Position cache cleared', {
          clearedKeys
        });
      }
    } catch (error) {
      logger.error('WealthicaStorage.clearAllPositionCacheKeys', 'Position cache clearing failed', { error });
    }
  }
};

// Initialize storage on module load
WealthicaStorage.initializeStorage();